import React from 'react';
import { styled } from '@mui/material/styles';

import { OrganisationAssignmentDto } from 'models/dtoTypes';
import Link from 'components/Link';
import {
  AcceptOrganisationAssignment,
  GetAllOrganisationAssignmentsAssignedToMe,
} from 'services/organisationAssignment';
import { Chip } from '@mui/material';
import { IColumn } from 'kosmos/components/ResourceTable';
import ResourceFullListTable from 'kosmos/components/ResourceFullListTable';
import Notice from 'kosmos/components/Notice';
import { useHistory } from 'react-router';
import { date2str } from 'utils/date';
import { useTranslation } from 'react-i18next';

const PREFIX = 'SmartGovernanceListPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function MySmartGovernances(): JSX.Element {
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const history = useHistory();
  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const { t } = useTranslation('default');

  const handleAcceptAssignment = async (model: OrganisationAssignmentDto) => {
    try {
      return await AcceptOrganisationAssignment(model.id);
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot accept organisation assignment',
        severity: 'error',
      });
    }
  };

  const handleContinueAnswerQuestions = (model: OrganisationAssignmentDto) => {
    // preview SME with current questionnaire
    history.push(`/smart_governances/${model.manualCompanyId || ''}/questionnaire/onboarding`);
  };

  const generateColumns = (): IColumn<OrganisationAssignmentDto>[] => [
    {
      label: t('organisation'),
      dataIndex: 'organisationId',
      sortIndex: 'organisationId',
      render: (model: OrganisationAssignmentDto) => {
        return model.organisation?.name || '(Unknown)';
      },
    },
    {
      label: t('assigner'),
      dataIndex: 'assignerId',
      sortIndex: 'assignerId',
      render: (model: OrganisationAssignmentDto) => {
        return model.assigner?.email || '';
      },
    },
    {
      label: t('coriolis-score'),
      dataIndex: 'kosmosCompany',
      sortIndex: 'kosmosCompany',
      render: (model: OrganisationAssignmentDto) => {
        if (model.kosmosCompanyScore) {
          return (
            <Link
              to={`/reports/${model.kosmosCompanyId || ''}`}
              text={model.kosmosCompanyScore.toString() || ''}
            />
          );
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('coriolis-company'),
      dataIndex: 'kosmosCompanyId',
      sortIndex: 'kosmosCompanyId',
      render: (model: OrganisationAssignmentDto) => {
        return (
          <Link
            to={`/reports/${model.kosmosCompanyId || ''}`}
            text={model.kosmosCompanyName || '(Unknown)'}
          />
        );
      },
    },
    {
      label: t('sg-score'),
      dataIndex: 'manualCompany',
      sortIndex: 'manualCompanyId',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.esgScore) {
          return (
            <Link
              to={`/combined-reports/${model.kosmosCompanyId || ''}`}
              text={model?.manualCompany?.esgScore.toString() || ''}
            />
          );
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('sg-scored-at'),
      dataIndex: 'manualCompany_scoredAt',
      sortIndex: '',
      render: (model: OrganisationAssignmentDto) => {
        if (model?.manualCompany?.lastScoredAt) {
          return date2str(model?.manualCompany?.lastScoredAt, 'YYYY-MM-DD HH:mm:ss');
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('is-accepted'),
      sortIndex: 'isAccepted',
      render: (model) => {
        const enabled = Boolean(model.isAccepted);
        return <Chip label={enabled ? 'Yes' : 'No'} color={enabled ? 'primary' : 'default'} />;
      },
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('accept'),
          hide: (model: OrganisationAssignmentDto) => model.isAccepted,
          onClick: handleAcceptAssignment,
        },
        {
          label: t('answer-questions'),
          hide: (model: OrganisationAssignmentDto) => !model.isAccepted || !model.manualCompany,
          onClick: handleContinueAnswerQuestions,
        },
      ],
    },
  ];

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('smart-governance-assignments')}</h2>
      </div>
      <ResourceFullListTable
        filterSchema={[
          {
            label: t('name-contains'),
            name: 'name',
            widget: 'text',
          },
        ]}
        columns={generateColumns()}
        onFetch={GetAllOrganisationAssignmentsAssignedToMe}
        defaultSort={{
          sortColumn: 'organisationId',
          isDesc: false,
        }}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
